import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { CheckoutOptimizationWidgetType } from "@redotech/redo-conversion-core-common/checkouts/widget";
import { CheckoutExperience } from "@redotech/redo-conversion-core-common/redo-in-shipping/ris";
import { CheckoutExperienceStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { memo, useMemo } from "react";
import { FrontendStepType, PartialWithRequiredType } from "..";
import { CheckoutExperienceContent } from "./checkout-experience-content";

const CheckoutExperiencePreview = memo(function CheckoutExperiencePreview({
  step,
  setStep,
  readonly,
}: {
  step: PartialWithRequiredType<CheckoutExperienceStep>;
  setStep: (step: PartialWithRequiredType<CheckoutExperienceStep>) => void;
  readonly?: boolean;
}) {
  const { t } = useTranslation();
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );

  const checkoutExperienceLoad = useLoad<
    CheckoutExperience | undefined
  >(async () => {
    if (!step.checkoutExperienceId) {
      return undefined;
    }
    const checkoutExperience: CheckoutExperience =
      await conversionRpcClient.getCheckoutExperienceById({
        checkoutExperienceId: step.checkoutExperienceId,
      });

    return checkoutExperience;
  }, [step.checkoutExperienceId]);

  const checkoutExperienceOptionsLoad = useLoad<
    {
      _id: string;
      name: string;
      widgets: CheckoutOptimizationWidgetType[];
      active: boolean;
    }[]
  >(async () => {
    const checkoutExperiences =
      await conversionRpcClient.getCheckoutExperiences({ active: undefined });
    return checkoutExperiences.map((checkoutExperience) => ({
      _id: checkoutExperience._id,
      name: checkoutExperience.name,
      widgets: checkoutExperience.widgets,
      active: checkoutExperience.active,
    }));
  }, []);

  const checkoutWidgetsLoad = useLoad(async () => {
    return await conversionRpcClient.getCheckoutWidgets({});
  }, []);

  const widgetsWithIds = useMemo<
    { id: string; type: CheckoutOptimizationWidgetType }[] | undefined
  >(() => {
    const widgetIds = checkoutExperienceLoad.value?.checkoutWidgetIds;
    if (!widgetIds || !checkoutWidgetsLoad.value) {
      return undefined;
    }
    const widgetMap = new Map(checkoutWidgetsLoad.value.map((w) => [w._id, w]));
    return widgetIds
      .map((id) => {
        const widget = widgetMap.get(id);
        return widget ? { id, type: widget.type } : null;
      })
      .filter(
        (w): w is { id: string; type: CheckoutOptimizationWidgetType } =>
          w !== null,
      );
  }, [checkoutExperienceLoad.value, checkoutWidgetsLoad.value]);

  const checkoutOptions = useMemo<TextRedoListItem<string>[]>(() => {
    if (!checkoutExperienceOptionsLoad.value) {
      return [];
    }

    return checkoutExperienceOptionsLoad.value.map((checkoutExperience) => ({
      id: checkoutExperience._id,
      value: checkoutExperience._id,
      type: "text",
      text: checkoutExperience.name,
      infoBadge: checkoutExperience.active
        ? { text: t("Active"), color: "success" as const }
        : { text: t("Inactive"), color: "gray" as const },
    }));
  }, [checkoutExperienceOptionsLoad.value, t]);

  const selectedOption = useMemo(() => {
    return checkoutOptions.find(
      (option) => option.value === step.checkoutExperienceId,
    );
  }, [checkoutOptions, step.checkoutExperienceId]);

  const pending =
    checkoutExperienceLoad.pending ||
    checkoutExperienceOptionsLoad.pending ||
    checkoutWidgetsLoad.pending;

  return (
    <Flex dir="column" gap="3xl" justify="flex-start">
      {pending ? (
        <LoadingRedoAnimation />
      ) : (
        <>
          {!readonly && !!checkoutExperienceOptionsLoad.value && (
            <RedoSingleSelectDropdownInput<string>
              label={t("Selected checkout experience")}
              options={checkoutOptions}
              optionSelected={({ item }) => {
                const selectedCheckoutExperience =
                  checkoutExperienceOptionsLoad.value?.find(
                    (checkoutExperience) =>
                      checkoutExperience._id === item.value,
                  );
                if (!selectedCheckoutExperience) {
                  return;
                }

                setStep({
                  ...step,
                  checkoutExperienceId: selectedCheckoutExperience._id,
                  name: selectedCheckoutExperience.name,
                  widgets: selectedCheckoutExperience.widgets,
                });
              }}
              selectedItem={selectedOption}
            />
          )}
          {!!checkoutExperienceLoad.value && (
            <CheckoutExperienceContent
              checkoutExperience={checkoutExperienceLoad.value}
              nodeId={step.id}
              readonly={!!readonly}
              showName={!!readonly}
              widgets={widgetsWithIds}
            />
          )}
        </>
      )}
    </Flex>
  );
});

export const checkoutExperienceStepType: FrontendStepType<
  PartialWithRequiredType<CheckoutExperienceStep>
> = {
  type: "standard",
  valid: (_) => true,
  selectable: (_) => true,
  inspectorFactory: (props) => ({
    header: { title: t("Checkout experience") },
    sections: [
      {
        fields: [
          {
            widget: "custom",
            Element: ({ value: step }) => (
              <CheckoutExperiencePreview
                readonly={props.readonly}
                setStep={props.setStep}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};
